import React, { memo, useCallback, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type SvgIcon from '@mui/material/SvgIcon';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import CloudUpload from '@mui/icons-material/CloudUpload';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import Dashboard from '@mui/icons-material/Dashboard';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';
import DataObject from '@mui/icons-material/DataObject';
import Description from '@mui/icons-material/Description';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import LocalShipping from '@mui/icons-material/LocalShipping';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import Menu from '@mui/icons-material/Menu';
import MenuOpen from '@mui/icons-material/MenuOpen';

export const NAV_WIDTH = 264;
export const NAV_COLLAPSED_WIDTH = 76;

interface NavItemDef {
  label: string;
  path: string;
  Icon: typeof SvgIcon;
  /** Filled variant shown while the item is active. */
  ActiveIcon?: typeof SvgIcon;
}

const MAIN_ITEMS: readonly NavItemDef[] = [
  { label: 'Dashboard', path: '/dashboard', Icon: DashboardOutlined, ActiveIcon: Dashboard },
  { label: 'Export partner data', path: '/export', Icon: CloudUploadOutlined, ActiveIcon: CloudUpload },
  { label: 'Print labels', path: '/label', Icon: LocalShippingOutlined, ActiveIcon: LocalShipping },
  { label: 'Mapping document', path: '/mapping-document', Icon: DescriptionOutlined, ActiveIcon: Description },
];

const RESOURCE_ITEMS: readonly NavItemDef[] = [
  { label: 'Swagger files', path: '/swagger-files', Icon: DataObject },
  { label: 'Data architecture', path: '/architecture', Icon: AccountTreeOutlined },
];

const NavItem = memo(function NavItem({ item, active, collapsed }: { item: NavItemDef; active: boolean; collapsed: boolean }) {
  const Icon = active && item.ActiveIcon ? item.ActiveIcon : item.Icon;
  return (
    <Tooltip title={collapsed ? item.label : ''} placement="right" disableInteractive>
      <ListItemButton
        component={RouterLink}
        to={item.path}
        aria-current={active ? 'page' : undefined}
        aria-label={collapsed ? item.label : undefined}
        sx={{
          flexGrow: 0,
          gap: 1.75,
          height: 44,
          px: 1.75,
          borderRadius: 22,
          whiteSpace: 'nowrap',
          color: active ? 'brand.onContainer' : 'text.secondary',
          bgcolor: active ? 'brand.container' : 'transparent',
          fontWeight: active ? 600 : 400,
          fontSize: 14,
          '&:hover': { bgcolor: active ? 'brand.container' : 'action.hover' },
        }}
      >
        <Icon sx={{ fontSize: 22, flexShrink: 0 }} />
        {collapsed ? null : <span>{item.label}</span>}
      </ListItemButton>
    </Tooltip>
  );
});

const isActive = (pathname: string, path: string) => pathname === path || pathname.startsWith(`${path}/`);

/** Label of the nav item that owns `pathname`, used for the header breadcrumb. */
const ALL_ITEMS: readonly NavItemDef[] = [...MAIN_ITEMS, ...RESOURCE_ITEMS];
export const navLabelFor = (pathname: string): string | undefined =>
  ALL_ITEMS.find((item) => isActive(pathname, item.path))?.label;

const Navigation: React.FC = () => {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const toggle = useCallback(() => setCollapsed((c) => !c), []);

  return (
    <Box
      component="aside"
      sx={{
        width: collapsed ? NAV_COLLAPSED_WIDTH : NAV_WIDTH,
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        transition: 'width .2s ease',
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
    >
      <Box sx={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pl: 2.5, pr: 1.5 }}>
        {collapsed ? null : (
          <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
            <Typography component="span" sx={{ fontWeight: 700, fontSize: 18, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Shipium
            </Typography>
            <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.15 }}>
              Partner Assistant
            </Typography>
          </Box>
        )}
        <IconButton
          onClick={toggle}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          aria-expanded={!collapsed}
          sx={{ width: 40, height: 40, flexShrink: 0, color: 'text.secondary' }}
        >
          {collapsed ? <Menu /> : <MenuOpen />}
        </IconButton>
      </Box>

      <Box component="nav" aria-label="Main" sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, p: 1.5 }}>
        {MAIN_ITEMS.map((item) => (
          <NavItem key={item.path} item={item} active={isActive(pathname, item.path)} collapsed={collapsed} />
        ))}
      </Box>

      <Box
        component="nav"
        aria-label="Resources"
        sx={{ mt: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25, borderTop: 1, borderColor: 'divider' }}
      >
        {collapsed ? null : (
          <Typography
            component="span"
            sx={{ px: 1.75, pt: 1, pb: 0.75, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: 'text.secondary' }}
          >
            RESOURCES
          </Typography>
        )}
        {RESOURCE_ITEMS.map((item) => (
          <NavItem key={item.path} item={item} active={isActive(pathname, item.path)} collapsed={collapsed} />
        ))}
      </Box>
    </Box>
  );
};

export default Navigation;
